/**
 * Maps Conversation API DTOs → app domain models used by Message UI / GiftedChat.
 */
import { ChatContentType } from '../../constants';
import { isGroupConversationType, isAiConversationType } from '../../constants/conversation';
import type {
  Conversation,
  ConversationMember,
  ExtendedChatMessage,
  MessageReplyTo,
} from '../../types';
import { getFullImageUrl } from '../../utils/imageUrl';
import { readModerationStatus } from '../moderation/moderationStatus';
import { IMAGE_BASE_URL } from '../../config/env';
import { normalizeConversation } from '../../utils/sortConversations';
import { formatContentTimestamp } from '../../utils/dateHelpers';
import {
  parseUtcTimestamp as parseUtcInstant,
  resolveLastMessageAt,
} from './lastMessageTimestamp';
import type {
  ConversationDto,
  ConversationListResponse,
  ConversationMessageDto,
  ConversationParticipantDto,
  ConversationSearchUserDto,
  UnreadCountResponse,
} from './conversationTypes';

/**
 * Parses a backend timestamp string as UTC, even when the string lacks an
 * explicit 'Z' suffix or timezone offset, falling back to now for empty or
 * unreadable values. Display converts to the viewer's timezone; the raw UTC
 * value is never mutated or persisted back.
 */
const parseUtcTimestamp = (value?: string | null): Date =>
  parseUtcInstant(value) ?? new Date();

/**
 * Resolve profile / conversation avatar paths from the API.
 * Absolute URLs pass through; relative paths are joined to IMAGE_BASE_URL
 * without forcing a UserImages/ prefix when the path already includes a folder.
 */
export const resolveAvatarUrl = (path?: string | null): string | null => {
  if (!path) return null;
  const trimmed = path.trim();
  if (!trimmed) return null;
  if (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('file://') ||
    trimmed.startsWith('content://') ||
    trimmed.startsWith('data:')
  ) {
    return trimmed;
  }

  const normalized = trimmed.replace(/\\/g, '/').replace(/^\/+/, '');

  // Paths that already include a storage folder should not get UserImages/ prepended.
  if (
    /^(UserImages|GroupImages|ConversationImages|Conversations|WLBImages|Uploads)\//i.test(
      normalized,
    )
  ) {
    const base = IMAGE_BASE_URL.replace(/\/+$/, '');
    return `${base}/${normalized}`;
  }

  return getFullImageUrl(normalized) || null;
};

/** Pick the first usable avatar field from a conversation DTO (list vs detail variance). */
const pickConversationAvatarPath = (dto: ConversationDto): string | null => {
  const record = dto as unknown as Record<string, unknown>;
  const candidates = [
    dto.avatarUrl,
    dto.AvatarUrl,
    dto.iconUrl,
    dto.imageUrl,
    dto.groupAvatarUrl,
    dto.profileImageUrl,
    record.groupImageUrl,
    record.GroupImageUrl,
    record.groupPhotoUrl,
    record.GroupPhotoUrl,
  ];
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return null;
};

export const mapParticipantToMember = (
  participant: ConversationParticipantDto,
): ConversationMember => ({
  id: String(participant.userId),
  name:
    participant.userName?.trim() ||
    `User ${participant.userId}`,
  username: participant.userName?.trim() || null,
  avatar: resolveAvatarUrl(participant.profileImageUrl),
  isAdmin: participant.isAdmin === true,
  profileUserGuid:
    typeof participant.userGuid === 'string' && participant.userGuid.trim()
      ? participant.userGuid.trim()
      : null,
});

const isGroupType = (type?: string | null): boolean =>
  isGroupConversationType(type);

const resolveDirectPeer = (
  dto: ConversationDto,
  currentUserId?: number | null,
): ConversationParticipantDto | null => {
  const participants = dto.participants ?? [];
  if (!participants.length) return null;
  if (currentUserId == null) return participants[0] ?? null;
  return (
    participants.find(p => Number(p.userId) !== Number(currentUserId)) ??
    participants[0] ??
    null
  );
};

const UNREAD_DTO_KEYS = [
  'unreadCount',
  'unreadMessageCount',
  'UnreadCount',
  'UnreadMessageCount',
  'unreadMessagesCount',
  'currentUserUnreadCount',
  'CurrentUserUnreadCount',
] as const;

/** Coerce API unread values (number or numeric string) to a count. */
const coerceUnreadCount = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, value);
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value.trim());
    if (Number.isFinite(parsed)) return Math.max(0, parsed);
  }
  return undefined;
};

/** Read unread count from list/detail DTOs (camelCase or PascalCase). */
export const readDtoUnreadCount = (
  dto: ConversationDto | Record<string, unknown> | null | undefined,
): number | undefined => {
  if (!dto || typeof dto !== 'object') return undefined;
  const record = dto as Record<string, unknown>;
  for (const key of UNREAD_DTO_KEYS) {
    const count = coerceUnreadCount(record[key]);
    if (count != null) return count;
  }
  return undefined;
};

const extractLastMessagePreview = (dto: ConversationDto): string =>
  dto.lastMessagePreview?.trim() ||
  dto.lastMessageText?.trim() ||
  dto.lastMessageBody?.trim() ||
  '';

export const mapConversationDto = (
  dto: ConversationDto,
  currentUserId?: number | null,
  prefs?: Partial<Conversation> | null,
): Conversation => {
  const participants = dto.participants ?? [];
  const apiMembers = participants.map(mapParticipantToMember);
  const group = isGroupType(dto.conversationType);
  // List payloads often omit participants; reuse cached members for collage avatars.
  const cachedMembers =
    group && Array.isArray(prefs?.members) && prefs.members.length > 0
      ? prefs.members
      : [];
  const members = apiMembers.length > 0 ? apiMembers : cachedMembers;
  const peer = group ? null : resolveDirectPeer(dto, currentUserId);
  const directUsername =
    peer?.userName?.trim() ||
    (!group && typeof dto.userName === 'string' && dto.userName.trim()
      ? dto.userName.trim()
      : null) ||
    prefs?.username?.trim() ||
    prefs?.userName?.trim() ||
    null;
  const displayName = group
    ? dto.name?.trim() ||
      members
        .filter(m => Number(m.id) !== Number(currentUserId))
        .map(m => m.name.split(' ')[0])
        .filter(Boolean)
        .join(', ') ||
      `Conversation ${dto.conversationId}`
    : directUsername ||
      `User ${peer?.userId ?? dto.conversationId}`;

  const apiAvatar = group
    ? resolveAvatarUrl(pickConversationAvatarPath(dto))
    : resolveAvatarUrl(peer?.profileImageUrl) ||
      resolveAvatarUrl(pickConversationAvatarPath(dto));
  const cachedAvatar =
    group && typeof prefs?.avatar === 'string' && prefs.avatar.trim()
      ? prefs.avatar.trim()
      : null;
  // Prefer API group photo; fall back to cached custom photo when list omits it.
  const avatar = group ? apiAvatar || cachedAvatar : apiAvatar;

  const lastMessage = extractLastMessagePreview(dto);
  const lastMessageSenderId = dto.lastMessage?.senderUserId;
  // Group and direct threads resolve the same way, from message activity only.
  const lastMessageAt = resolveLastMessageAt(dto);
  const hasLastMessage = Boolean(lastMessage?.trim());
  // A media-only last message has no preview text but is still activity, so
  // the row keeps its timestamp and its place in the ordering.
  const hasMessageActivity = lastMessageAt > 0 || hasLastMessage;

  const currentParticipant = participants.find(
    p => currentUserId != null && Number(p.userId) === Number(currentUserId),
  );

  const apiUnreadCount = readDtoUnreadCount(dto);
  const isUnread =
    apiUnreadCount != null ? apiUnreadCount > 0 : undefined;
  const unreadCount = apiUnreadCount;

  const participantUserIds =
    participants.length > 0
      ? participants.map(p => Number(p.userId))
      : members
          .map(m => Number(m.id))
          .filter(id => !Number.isNaN(id));

  const resolvedUserName = group
    ? null
    : directUsername || prefs?.userName?.trim() || prefs?.username?.trim() || null;

  return normalizeConversation({
    id: String(dto.conversationId),
    // User-created titles apply only to groups. A stale direct-chat title such
    // as "Chat" must never override the peer's username.
    name: group ? prefs?.name?.trim() || displayName : displayName,
    username: resolvedUserName,
    userName: resolvedUserName,
    groupName: group
      ? prefs?.groupName?.trim() ||
        prefs?.name?.trim() ||
        dto.name?.trim() ||
        null
      : null,
    avatar,
    members,
    memberCount: members.length || prefs?.memberCount || 0,
    createdBy:
      dto.createdByUserId != null ? String(dto.createdByUserId) : null,
    createdAt: dto.createdAt ? parseUtcTimestamp(dto.createdAt).getTime() : undefined,
    lastMessage: hasLastMessage ? lastMessage : '',
    // Only the nested message object names its sender; list payloads that omit
    // it leave the row unattributed rather than guessing.
    lastMessageByMe:
      lastMessageSenderId != null && currentUserId != null
        ? Number(lastMessageSenderId) === Number(currentUserId)
        : false,
    time: lastMessageAt > 0 ? formatContentTimestamp(lastMessageAt, 'utc') : '',
    lastMessageAt: hasMessageActivity ? lastMessageAt : 0,
    isOnline: false,
    isUnread,
    unreadCount,
    unreadCountReported: apiUnreadCount != null,
    // AI agent threads must not appear as normal buddy DMs in the inbox.
    category: group
      ? 'groups'
      : isAiConversationType(dto.conversationType)
        ? 'ai'
        : 'buddies',
    conversationType: dto.conversationType ?? null,
    participantUserIds,
    peerUserId: peer?.userId != null ? String(peer.userId) : prefs?.peerUserId ?? null,
    peerUserGuid:
      (typeof peer?.userGuid === 'string' && peer.userGuid.trim()
        ? peer.userGuid.trim()
        : null) ||
      prefs?.peerUserGuid ||
      null,
    lastMessageId:
      dto.lastMessageId != null ? String(dto.lastMessageId) : null,
    isPinned: prefs?.isPinned ?? false,
    pinnedAt: prefs?.pinnedAt,
    isMuted: prefs?.isMuted ?? currentParticipant?.isMuted ?? false,
    isFavourite: prefs?.isFavourite ?? false,
    isBlocked: prefs?.isBlocked ?? false,
    isActive: dto.isActive !== false,
  });
};

export const extractConversationList = (
  payload: ConversationListResponse | null | undefined,
): ConversationDto[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.conversations)) return payload.conversations;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

export const extractListPagination = (
  payload: ConversationListResponse | null | undefined,
  pageNumber: number,
  pageSize: number,
  itemCount: number,
): { totalCount: number; totalPages: number; hasNextPage: boolean } => {
  if (!payload || Array.isArray(payload)) {
    return {
      totalCount: itemCount,
      totalPages: itemCount < pageSize ? pageNumber : pageNumber + 1,
      hasNextPage: itemCount >= pageSize,
    };
  }
  const totalCount = payload.totalCount ?? itemCount;
  const totalPages =
    payload.totalPages ??
    Math.max(1, Math.ceil(totalCount / (payload.pageSize ?? pageSize)));
  const currentPage = payload.pageNumber ?? pageNumber;
  return {
    totalCount,
    totalPages,
    hasNextPage: currentPage < totalPages,
  };
};

export const parseUnreadCount = (
  payload: UnreadCountResponse | null | undefined,
): number => {
  const directValue = coerceUnreadCount(payload);
  if (directValue != null) return directValue;
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    const nested = coerceUnreadCount(record.data);
    if (nested != null) return nested;
    if (record.data && typeof record.data === 'object') {
      const nestedCount = readDtoUnreadCount(
        record.data as Record<string, unknown>,
      );
      if (nestedCount != null) return nestedCount;
    }
    const fromDto = readDtoUnreadCount(record);
    if (fromDto != null) return fromDto;
    const count =
      coerceUnreadCount(record.count) ?? coerceUnreadCount(record.Count);
    if (count != null) return count;
  }
  return 0;
};

const mapMessageTypeToGiftedFields = (
  message: ConversationMessageDto,
): Pick<ExtendedChatMessage, 'image' | 'video' | 'type' | 'fileName'> => {
  const type = (message.messageType || '').toLowerCase();
  const first = message.attachments?.[0];
  const fileUrl = resolveAvatarUrl(first?.fileUrl) || undefined;
  const fileName = first?.fileName || undefined;

  if (type.includes('image') || type.includes('gif')) {
    // Fallback: when the API doesn't persist the attachment, the image URL
    // may be in bodyText (sent there for exactly this recovery scenario).
    const imageUrl = fileUrl
      || (message.bodyText && /^https?:\/\//i.test(message.bodyText.trim()) ? message.bodyText.trim() : undefined);
    return { image: imageUrl, type: type.includes('gif') ? 'gif' : 'image', fileName };
  }
  if (type.includes('video')) {
    return { video: fileUrl, type: 'video', fileName };
  }
  if (type.includes('file') && fileUrl) {
    return { type: 'file', fileName: fileName || 'file', image: undefined };
  }

  if (fileUrl) {
    const contentTypeId = first?.contentTypeId;
    if (contentTypeId === ChatContentType.Image) {
      return { image: fileUrl, type: 'image', fileName };
    }
    if (contentTypeId === ChatContentType.Video) {
      return { video: fileUrl, type: 'video', fileName };
    }
    if (contentTypeId === ChatContentType.File) {
      return { type: 'file', fileName: fileName || 'file' };
    }
    return { image: fileUrl, type: 'image', fileName };
  }

  return {};
};

const mapReplyContext = (
  message: ConversationMessageDto,
): MessageReplyTo | null => {
  if (!message.replyContext && message.replyToMessageId == null) {
    return null;
  }
  const bodyText = message.replyContext?.bodyText?.trim() || '';
  const senderUsername = message.replyContext?.senderUserName?.trim() || null;
  return {
    _id: message.replyContext?.messageId ?? message.replyToMessageId ?? undefined,
    text: bodyText,
    senderName: senderUsername ?? undefined,
    senderUsername,
    type: message.replyContext?.messageType ?? undefined,
  };
};

/**
 * Backend message id as a string, or null when the payload does not carry a
 * usable one. Never returns the literal `"undefined"`, which would otherwise
 * be interpolated into `MarkMessageRead/{messageId}` and 404.
 */
export const resolveMessageId = (
  message: ConversationMessageDto | null | undefined,
): string | null => {
  // Typed as a required number, but the field is absent on some responses —
  // treat it as untrusted rather than letting String() emit "undefined".
  const raw = message?.messageId as unknown;
  if (raw == null || raw === '') return null;
  const numeric = Number(raw);
  return Number.isFinite(numeric) && numeric > 0 ? String(numeric) : null;
};

const LOCAL_MESSAGE_ID_PREFIX = 'local-msg-';

/** Local placeholder id for a message the backend echoed back without one. */
export const generateLocalMessageId = (): string =>
  `${LOCAL_MESSAGE_ID_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

/** True for optimistic rows that exist only on this client. */
export const isLocalMessageId = (id: string | number | null | undefined): boolean =>
  typeof id === 'string' && id.startsWith(LOCAL_MESSAGE_ID_PREFIX);

export const unwrapSendMessageDto = (
  data: unknown,
): ConversationMessageDto | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const record = data as Record<string, unknown>;

  const asMessageDto = (
    value: unknown,
  ): ConversationMessageDto | null => {
    if (!value || typeof value !== 'object') {
      return null;
    }
    const candidate = value as Record<string, unknown>;
    if (
      'messageId' in candidate ||
      'senderUserId' in candidate ||
      'bodyText' in candidate
    ) {
      return candidate as unknown as ConversationMessageDto;
    }
    return null;
  };

  if ('message' in record) {
    const nested = asMessageDto(record.message);
    if (nested) {
      return nested;
    }
  }

  if ('data' in record) {
    const inner = record.data;
    if (inner && typeof inner === 'object') {
      const innerRecord = inner as Record<string, unknown>;
      if ('message' in innerRecord) {
        const nested = asMessageDto(innerRecord.message);
        if (nested) {
          return nested;
        }
      }
      const direct = asMessageDto(inner);
      if (direct) {
        return direct;
      }
    }
  }

  return asMessageDto(record);
};

export const mapMessageDto = (
  message: ConversationMessageDto,
  currentUserId?: number | null,
): ExtendedChatMessage => {
  const media = mapMessageTypeToGiftedFields(message);
  // Backend returns UTC timestamps — parse as UTC and convert to local only for display.
  const createdAt = message.sentAt ? parseUtcTimestamp(message.sentAt) : new Date();
  const messageId = resolveMessageId(message);
  const senderUserId = message.senderUserId as unknown;
  const hasSenderUserId =
    senderUserId != null &&
    senderUserId !== '' &&
    Number.isFinite(Number(senderUserId));
  const isMine =
    currentUserId != null &&
    hasSenderUserId &&
    Number(senderUserId) === Number(currentUserId);

  return {
    // A missing id means the response shape was not what we expect; keep the
    // row renderable with a local key and let the next fetch reconcile it.
    _id: messageId ?? generateLocalMessageId(),
    // When bodyText is a URL used as image fallback, don't show it as text.
    text: media.image && message.bodyText && /^https?:\/\//i.test(message.bodyText.trim())
      ? ''
      : (message.bodyText ?? ''),
    createdAt,
    user: {
      _id: isMine
        ? (currentUserId as number)
        : hasSenderUserId
          ? Number(senderUserId)
          : '',
      name: message.senderUserName ?? undefined,
      username: message.senderUserName?.trim() || null,
      avatar: resolveAvatarUrl(message.senderAvatarUrl) ?? undefined,
    },
    replyTo: mapReplyContext(message),
    ...media,
    // Preserve AI markers so human chats can filter agent auto-replies.
    type: media.type ?? message.messageType ?? undefined,
    isAgentMessage: message.isAgentMessage === true,
    // Drives the read receipts sent when the thread is opened.
    isReadByCurrentUser: message.isReadByCurrentUser === true,
    // Without a backend id the row is not confirmed server-side yet.
    pending: messageId == null,
    sent: messageId != null,
    received: messageId != null,
    moderationStatus: readModerationStatus(message),
    // Outgoing messages: seen when at least one other user has a read receipt.
    // readReceipts is an array of per-user receipts from the backend.
    ...(isMine && message.readReceipts && message.readReceipts.length > 0
      ? { seen: true, readReceipts: message.readReceipts }
      : {}),
  };
};

export const mapSearchUserToMember = (
  user: ConversationSearchUserDto,
): ConversationMember & {
  isOnline?: boolean;
  hasExistingConversation?: boolean;
  email?: string | null;
} => ({
  id: String(user.userId),
  name: user.userName?.trim() || `User ${user.userId}`,
  username: user.userName?.trim() || null,
  avatar: resolveAvatarUrl(user.avatarUrl),
  isOnline: !!user.isOnline,
  hasExistingConversation: !!user.hasExistingConversation,
  email: user.email ?? null,
});

/**
 * Builds a unique selectable-member list from Conversation/List rows.
 * Prefers direct-chat peers; also includes other participants from group chats.
 */
export const extractSelectableMembers = (
  conversations: Conversation[],
  currentUserId?: number | null,
  searchTerm?: string,
): ConversationMember[] => {
  const byId = new Map<string, ConversationMember>();

  const upsert = (member: ConversationMember) => {
    if (!member?.id) return;
    if (
      currentUserId != null &&
      String(member.id) === String(currentUserId)
    ) {
      return;
    }
    const existing = byId.get(member.id);
    if (!existing) {
      byId.set(member.id, {
        id: String(member.id),
        name: member.name || `User ${member.id}`,
        username: member.username ?? null,
        avatar: member.avatar ?? null,
        profileUserGuid: member.profileUserGuid ?? null,
      });
      return;
    }
    // Prefer entries that already have an avatar / clearer name.
    byId.set(member.id, {
      id: existing.id,
      name:
        existing.name && !existing.name.startsWith('User ')
          ? existing.name
          : member.name || existing.name,
      username: existing.username || member.username || null,
      avatar: existing.avatar || member.avatar || null,
      profileUserGuid:
        existing.profileUserGuid || member.profileUserGuid || null,
    });
  };

  for (const conversation of conversations) {
    if (conversation.isBlocked) continue;

    if (
      conversation.category !== 'groups' &&
      conversation.peerUserId
    ) {
      upsert({
        id: String(conversation.peerUserId),
        name: conversation.name,
        username: conversation.username ?? null,
        avatar: conversation.avatar ?? null,
        profileUserGuid: conversation.peerUserGuid ?? null,
      });
    }

    for (const member of conversation.members ?? []) {
      upsert(member);
    }
  }

  let members = Array.from(byId.values());
  const query = searchTerm?.trim().toLowerCase();
  if (query) {
    members = members.filter(m => m.name.toLowerCase().includes(query));
  }

  return members.sort((a, b) => a.name.localeCompare(b.name));
};

export const resolveMessageTypeId = (_options?: {
  hasImage?: boolean;
  hasVideo?: boolean;
  hasFile?: boolean;
  hasGif?: boolean;
}): number => {
  // Media upload is not wired yet — always send Text (1).
  // When MEDIA_UPLOAD_ENABLED + upload API are ready, restore:
  // if (_options?.hasGif || _options?.hasImage) return ChatContentType.Image;
  // if (_options?.hasVideo) return ChatContentType.Video;
  // if (_options?.hasFile) return ChatContentType.File;
  return ChatContentType.Text;
};

export const isRemoteMediaUrl = (url?: string | null): boolean => {
  if (!url) return false;
  return /^https?:\/\//i.test(url);
};
